// ═══════════════════════════════════════════════════════════
// Mantine Dates Shim — drop-in replacements for @mantine/dates
// Uses native <input type="date"> and <input type="time">
// ═══════════════════════════════════════════════════════════

import React, { type CSSProperties } from 'react';

type MantineSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | (string & {}) | number | undefined;
function sp(val: MantineSize): string | undefined {
  const map: Record<string, string> = {
    xs: '0.25rem', sm: '0.5rem', md: '1rem', lg: '1.5rem', xl: '2rem',
  };
  if (!val) return undefined;
  if (typeof val === 'number') return `${val * 0.25}rem`;
  return map[String(val)] ?? val;
}

interface CommonDateProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  size?: MantineSize;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  m?: MantineSize; w?: MantineSize; flex?: string | number; miw?: MantineSize;
  style?: CSSProperties;
  className?: string;
  id?: string;
  withAsterisk?: boolean;
  placeholder?: string;
  clearable?: boolean;
  popoverProps?: Record<string, unknown>;
  valueFormat?: string;
  firstDayOfWeek?: number;
  weekendDays?: number[];
  highlightToday?: boolean;
  getDayProps?: (date: Date) => Record<string, unknown>;
  excludeDate?: (date: Date) => boolean;
  minDate?: Date;
  maxDate?: Date;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  styles?: Record<string, unknown>;
}

// ISO string ↔ native date input value converter
function dateToInput(d: Date | string | null | undefined): string {
  if (!d) return '';
  if (typeof d === 'string') {
    // already ISO or YYYY-MM-DD
    const match = d.match(/(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }
  return d.toISOString().slice(0, 10);
}

function inputToDate(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// ── DateInput ──────────────────────────────────────────────

interface DateInputProps extends CommonDateProps {
  value?: Date | string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (date: any) => void;
  defaultValue?: Date | string | null;
}

export function DateInput({
  label, description, error, required, disabled, value, onChange, defaultValue,
  mt, mb, mx, my, m, w, flex, miw, withAsterisk, placeholder,
  style, className = '', id,
}: DateInputProps) {
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(flex !== undefined ? { flex } : {}),
  };
  const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const inputVal = value !== undefined ? dateToInput(value) : defaultValue !== undefined ? dateToInput(defaultValue) : undefined;

  return (
    <div className={`form-group ${className}`} style={wrapStyle}>
      {label && (
        <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>
          {label}
        </label>
      )}
      {description && <span className="form-help">{description}</span>}
      <input
        type="date"
        id={inputId}
        className={`form-input${error ? ' error' : ''}`}
        value={inputVal ?? ''}
        placeholder={placeholder}
        disabled={disabled}
        style={style}
        onChange={e => onChange?.(inputToDate(e.target.value))}
      />
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

// ── DatePickerInput ─────────────────────────────────────────

interface DatePickerInputProps extends CommonDateProps {
  type?: 'default' | 'range' | 'multiple';
  value?: Date | string | null | [Date | null, Date | null];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (date: any) => void;
  defaultValue?: Date | string | null;
}

export function DatePickerInput({
  label, description, error, required, disabled, value, onChange, type = 'default',
  mt, mb, mx, my, m, w, flex, miw, withAsterisk, placeholder,
  style, className = '', id,
}: DatePickerInputProps) {
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(miw ? { minWidth: typeof miw === 'number' ? `${miw}px` : miw } : {}),
    ...(flex !== undefined ? { flex } : {}),
  };
  const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  if (type === 'range') {
    const range = Array.isArray(value) ? value : [null, null];
    return (
      <div className={`form-group ${className}`} style={wrapStyle}>
        {label && (
          <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>
            {label}
          </label>
        )}
        {description && <span className="form-help">{description}</span>}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input type="date" className={`form-input${error ? ' error' : ''}`}
            value={dateToInput(range[0])} disabled={disabled}
            onChange={e => (onChange as any)?.([inputToDate(e.target.value), range[1]])}
            style={style}
          />
          <span style={{ color: 'var(--color-neutral-500)', flexShrink: 0 }}>–</span>
          <input type="date" className={`form-input${error ? ' error' : ''}`}
            value={dateToInput(range[1])} disabled={disabled}
            onChange={e => (onChange as any)?.([range[0], inputToDate(e.target.value)])}
            style={style}
          />
        </div>
        {error && <span className="form-error" role="alert">{error}</span>}
      </div>
    );
  }

  const singleVal = Array.isArray(value) ? null : value;
  return (
    <div className={`form-group ${className}`} style={wrapStyle}>
      {label && (
        <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>
          {label}
        </label>
      )}
      {description && <span className="form-help">{description}</span>}
      <input
        type="date"
        id={inputId}
        className={`form-input${error ? ' error' : ''}`}
        value={dateToInput(singleVal)}
        placeholder={placeholder}
        disabled={disabled}
        style={style}
        onChange={e => (onChange as any)?.(inputToDate(e.target.value))}
      />
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

// ── DateTimePicker ──────────────────────────────────────────

interface DateTimePickerProps extends CommonDateProps {
  value?: Date | string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (date: any) => void;
  defaultValue?: Date | string | null;
  withSeconds?: boolean;
  submitButtonProps?: Record<string, unknown>;
  clearButtonProps?: Record<string, unknown>;
  timeInputProps?: Record<string, unknown>;
}

export function DateTimePicker({
  label, description, error, required, disabled, value, onChange,
  mt, mb, mx, my, m, w, flex, withAsterisk, placeholder,
  style, className = '', id, withSeconds,
}: DateTimePickerProps) {
  const wrapStyle: CSSProperties = {
    ...(mt ? { marginTop: sp(mt) } : {}),
    ...(mb ? { marginBottom: sp(mb) } : {}),
    ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
    ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
    ...(m ? { margin: sp(m) } : {}),
    ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
    ...(flex !== undefined ? { flex } : {}),
  };
  const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  const dtVal = value
    ? (typeof value === 'string' ? value : value.toISOString()).slice(0, withSeconds ? 19 : 16)
    : '';

  return (
    <div className={`form-group ${className}`} style={wrapStyle}>
      {label && (
        <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>
          {label}
        </label>
      )}
      {description && <span className="form-help">{description}</span>}
      <input
        type="datetime-local"
        id={inputId}
        className={`form-input${error ? ' error' : ''}`}
        value={dtVal}
        placeholder={placeholder}
        disabled={disabled}
        style={style}
        step={withSeconds ? 1 : undefined}
        onChange={e => {
          if (!e.target.value) { onChange?.(null); return; }
          onChange?.(new Date(e.target.value));
        }}
      />
      {error && <span className="form-error" role="alert">{error}</span>}
    </div>
  );
}

// ── TimeInput ───────────────────────────────────────────────

interface TimeInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange' | 'value'> {
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  size?: MantineSize;
  withSeconds?: boolean;
  mt?: MantineSize; mb?: MantineSize; mx?: MantineSize; my?: MantineSize;
  w?: MantineSize; flex?: string | number;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  withAsterisk?: boolean;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  styles?: Record<string, unknown>;
}

export const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(
  function TimeInput({
    label, description, error, required, withSeconds, value, onChange, withAsterisk,
    mt, mb, mx, my, w, flex, style, className = '', id,
    size: _size, leftSection: _ls, rightSection: _rs, styles: _styles,
    ...rest
  }, ref) {
    const wrapStyle: CSSProperties = {
      ...(mt ? { marginTop: sp(mt) } : {}),
      ...(mb ? { marginBottom: sp(mb) } : {}),
      ...(mx ? { marginLeft: sp(mx), marginRight: sp(mx) } : {}),
      ...(my ? { marginTop: sp(my), marginBottom: sp(my) } : {}),
      ...(w ? { width: typeof w === 'number' ? `${w}px` : w } : {}),
      ...(flex !== undefined ? { flex } : {}),
    };
    const inputId = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    return (
      <div className={`form-group ${className}`} style={wrapStyle}>
        {label && (
          <label className={`form-label${required || withAsterisk ? ' required' : ''}`} htmlFor={inputId}>
            {label}
          </label>
        )}
        {description && <span className="form-help">{description}</span>}
        <input
          ref={ref}
          type="time"
          id={inputId}
          className={`form-input${error ? ' error' : ''}`}
          value={value ?? ''}
          onChange={onChange}
          step={withSeconds ? 1 : undefined}
          style={style}
          {...rest}
        />
        {error && <span className="form-error" role="alert">{error}</span>}
      </div>
    );
  }
);
